import {
  ActionPlanDetailsType,
  ActionPlanItemsUpsertPayloadType,
  ActionPlanListItemType,
  ActionPlanStepListItemType,
} from 'types/actionPlans';
import { UserActorType } from 'types/user';

import { ActionPlanFormValues } from './CreateEditActionPlansModalTypes';

export const getUserDisplayName = (user: UserActorType) => {
  const name = user.name?.trim();
  if (name) {
    return name;
  }

  const fullName = user.full_name?.trim();
  if (fullName) {
    return fullName;
  }

  const combinedName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return combinedName || user.email?.trim() || user.username?.trim() || 'Unknown User';
};

export const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

export const isFormValidationError = (error: unknown): error is { errorFields: unknown[] } => {
  return typeof error === 'object' && error !== null && Array.isArray((error as { errorFields?: unknown }).errorFields);
};

export const getCurrentUserDisplayName = (
  user?: {
    FullName?: string;
    fname?: string;
    name?: string;
    email?: string;
  } | null
) => {
  if (!user) {
    return '';
  }

  return user.FullName?.trim() || user.fname?.trim() || user.name?.trim() || user.email?.trim() || '';
};

export const sortActionPlanSteps = (steps: ActionPlanStepListItemType[] = []) =>
  [...steps].sort((leftStep, rightStep) => leftStep.id - rightStep.id);

type ActionPlanFormItem = NonNullable<ActionPlanFormValues['items']>[number];
type ValidActionPlanFormItem = ActionPlanFormItem & { type_id: number };

const formatActionPlanItemDueDate = (value: ActionPlanFormItem['due_date']) =>
  value ? value.format('YYYY-MM-DD') : null;

const normalizeComparableText = (value?: string | null) => (value ?? '').trim();

export const hasActionPlanContentChanges = (
  values: ActionPlanFormValues,
  plan?: ActionPlanDetailsType | ActionPlanListItemType | null
) => {
  if (!plan) {
    return false;
  }

  return (
    normalizeComparableText(values.name) !== normalizeComparableText(plan.name) ||
    normalizeComparableText(values.description) !== normalizeComparableText(plan.description) ||
    toNumberOrUndefined(values.focus_area_id) !== toNumberOrUndefined(plan.focus_area_id)
  );
};

export const hasActionPlanItemContentChanges = (
  item: NonNullable<ActionPlanFormValues['items']>[number],
  step?: ActionPlanStepListItemType
) => {
  if (!step) {
    return false;
  }

  return (
    normalizeComparableText(item.action) !== normalizeComparableText(step.action) ||
    toNumberOrUndefined(item.type_id) !== toNumberOrUndefined(step.type_id) ||
    toNumberOrUndefined(item.owner_id) !== toNumberOrUndefined(step.owner?.id) ||
    formatActionPlanItemDueDate(item.due_date) !== (step.due_date ?? null)
  );
};

export const getSubmittedActionPlanItems = (items?: ActionPlanFormValues['items']): ValidActionPlanFormItem[] =>
  (items ?? []).filter(
    (item): item is ValidActionPlanFormItem => Boolean(item.action?.trim()) && typeof item.type_id === 'number'
  );

export const getKeptActionPlanStepIds = (items?: ActionPlanFormValues['items']) =>
  (items ?? []).map((item) => item.id).filter((id): id is number => typeof id === 'number');

export const getActionPlanItemsPayload = (
  items: ValidActionPlanFormItem[],
  newItemStatus: number,
  currentSteps: ActionPlanStepListItemType[] = []
): ActionPlanItemsUpsertPayloadType[] =>
  items
    .filter((item) => {
      if (typeof item.id !== 'number') {
        return true;
      }

      const currentStep = currentSteps.find((step) => step.id === item.id);
      return hasActionPlanItemContentChanges(item, currentStep);
    })
    .map((item) => ({
      ...(typeof item.id === 'number' ? { id: item.id } : {}),
      action: item.action,
      type_id: item.type_id,
      owner_id: item.owner_id ?? null,
      due_date: formatActionPlanItemDueDate(item.due_date),
      ...(typeof item.id === 'number' ? {} : { status: newItemStatus }),
    }));

export const getNewActionPlanItemsPayload = (
  items: ValidActionPlanFormItem[],
  status: number
): ActionPlanItemsUpsertPayloadType[] =>
  items
    .filter((item) => typeof item.id !== 'number')
    .map((item) => ({
      action: item.action,
      type_id: item.type_id,
      owner_id: item.owner_id ?? null,
      due_date: formatActionPlanItemDueDate(item.due_date),
      status,
    }));

export const getActionPlanDeleteMessage = (planName: string) =>
  `You are about to delete the action plan '${planName}'. This cannot be undone.`;
